import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "@/config/constants";

export const HeroSection = () => {
  // Navegação interna suave com ajuste de offset para o header
  const scrollToNextSection = () => {
    const nextSection = document.getElementById("sobre");
    if (nextSection) {
      const yOffset = -80;
      const y =
        nextSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      id="inicio"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-[#192338]"
    >
      {/* Background: Imagem com overlay de baixa opacidade e glow radial centralizado */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/office.webp')`,
            backgroundAttachment: "scroll",
          }}
        />
        <div className="absolute inset-0 bg-[#192338]/85 backdrop-blur-[2px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_rgba(221,195,133,0.12)_0%,_transparent_60%)] opacity-70 pointer-events-none" />
      </div>

      <div className="relative z-10 container mx-auto px-4 flex flex-col items-center text-center py-10">
        {/* Detalhe Estético: Eixo vertical superior com gradiente descendente */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="hidden sm:flex flex-col items-center mb-10"
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent to-[#DDC385]/60" />
        </motion.div>

        {/* Headline: Tipografia rica com mix de itálico e gradientes dourados */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl w-full"
        >
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-[1.1] mb-6 md:mb-8 tracking-tight">
            Soluções para{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DDC385] via-[#f3e1b5] to-[#DDC385] italic font-serif font-light">
              organização
            </span>{" "}
            e <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DDC385] via-[#f3e1b5] to-[#DDC385]">
              crescimento.
            </span>
          </h1>

          {/* Linha de gradiente horizontal animada (h-px) */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: 160 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="h-px bg-gradient-to-r from-transparent via-[#DDC385]/50 to-transparent mx-auto mb-8 md:mb-10 max-w-[40%]"
          />
        </motion.div>

        {/* Descrição: Texto com tracking wide para leveza na leitura */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="text-white/60 text-base md:text-xl leading-relaxed mb-10 md:mb-12 max-w-2xl font-light tracking-wide"
        >
          Consultoria especializada para empresas que buscam eficiência
          operacional e{" "}
          <span className="relative inline-block text-white/90 font-medium">
            resultados concretos
            <span className="absolute bottom-0 left-0 w-full h-[1px] bg-white/20" />
          </span>{" "}
          com rigor executivo.
        </motion.p>

        {/* CTAs: Botões com sombras dinâmicas, hover animado e tracking institucional */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 w-full px-2"
        >
          {/* Botão Primário: Sombra projetada no hover para destaque visual */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full max-w-[300px] sm:w-auto group relative px-6 md:px-10 py-5 bg-[#DDC385] text-[#192338] font-bold uppercase text-[9px] md:text-[11px] tracking-[0.2em] transition-all duration-500 hover:shadow-[0_0_30px_rgba(221,195,133,0.4)] flex items-center justify-center gap-3 whitespace-nowrap"
          >
            <MessageCircle size={18} className="shrink-0" />
            <span>Fale com um especialista</span>
          </a>

          {/* Botão Secundário: Borda fina e background sutil no hover */}
          <button
            onClick={scrollToNextSection}
            className="w-full max-w-[300px] sm:w-auto group relative px-6 md:px-10 py-5 border border-white/10 text-white uppercase text-[9px] md:text-[11px] tracking-[0.2em] transition-all duration-300 hover:bg-white/[0.03] overflow-hidden flex items-center justify-center gap-3 whitespace-nowrap"
          >
            <span className="relative z-10 flex items-center gap-2">
              Nossos Serviços{" "}
              <ArrowRight
                size={16}
                className="shrink-0 transition-transform group-hover:translate-x-1"
              />
            </span>
          </button>
        </motion.div>
      </div>

      {/* Explore: Gatilho visual inferior com tracking extremo e animação bounce */}
      <motion.button
        onClick={scrollToNextSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 group cursor-pointer z-20 p-4"
      >
        <span className="hidden md:block text-[#DDC385]/40 text-[9px] tracking-[0.6em] uppercase group-hover:text-[#DDC385] transition-colors">
          Explore
        </span>
        <div className="relative">
          <ChevronDown
            size={30}
            className="text-[#DDC385]/60 animate-bounce group-hover:text-[#DDC385]"
            strokeWidth={1.2}
          />
        </div>
      </motion.button>

      {/* Linha final: Gradiente sutil para encerramento do viewport principal */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#DDC385]/20 to-transparent" />
    </section>
  );
};
